/**
 * Get it built API (docs/marketplace_get_it_built.md) — `/api/marketplace/build/*`.
 *
 * The client pays TWASTA in the instance admin's standard stages; the builder
 * builds in their own account; at sign-off the app is installed in the
 * client's organization as a private Marketplace listing. Every amount is in
 * MINOR units (paise / cents) — format with `money()` from '@/api/marketplace'.
 *
 * Errors: `{ detail: { code: 'BUILD_*', message, ...extra } }` — read them with
 * `mktError()` from '@/api/marketplace' (message = plain words to show).
 */
import api from './client'
import type { BuildTerms } from './marketplace'

const BASE = '/marketplace/build'

// ── shared shapes ──────────────────────────────────────────────────────────

export interface BuilderRef { tenant_id: string; user_id: string | null; name: string; person: string; headline: string; initials: string }
export interface ClientCharge { base_minor: number; service_fee_minor: number; total_minor: number; service_fee_percent: number }
export interface Payout { gross_minor: number; fee_minor: number; fee_tax_minor: number; fee_tax_breakdown: { name: string; rate: number; amount_minor: number }[]; tds_minor: number; net_minor: number; fee_percent: number; tds_percent: number }
export interface Monthly { basis: 'person' | 'server'; price_minor: number; size?: string; seats?: number }
export interface TimelineEvent { id: number; kind: 'message' | 'event' | 'review'; text: string; at: string; actor_user_id: string | null; actor: string; data: Record<string, any> }

// ── hosting (public) ───────────────────────────────────────────────────────

export interface HostingSize { id: string; name: string; max_people: number; price_minor: number; server_cost_minor: number; machines: Record<string, string>; storage_gb: number; files_gb: number; limits: Record<string, number>; market_reference: Record<string, string> }
export interface HostingPrices {
  currency: string
  per_person_minor: number
  sizes: HostingSize[]
  /** The cheapest option — "Host your app starting …" */
  start: { amount_minor: number; basis: 'person' | 'server' }
  management_percent: number
  margin_percent: number
  per_person_tier: Record<string, { base: number; per_person: number; max: number }>
}
export async function getHosting(currency = 'INR'): Promise<HostingPrices> {
  const { data } = await api.get(`${BASE}/hosting`, { params: { currency } })
  return data
}

// ── requests (client) ──────────────────────────────────────────────────────

export type RequestKind = 'new' | 'change' | 'site' | 'analytics'
export type FeatureKey = 'phone' | 'offline' | 'approvals' | 'alerts' | 'import' | 'dash' | 'portal' | 'integrate'
export interface BriefSection { h: string; items: string[] }
export interface RequestDetails {
  integration?: { system: string; version?: string; direction: 'in' | 'out' | 'both'; what?: string; access?: string; docs?: string }
  analytics?: { db: string; app?: string; size?: string; access?: string }
  users?: string
  data?: string
}
export interface RequestInput {
  title?: string
  description?: string
  kind?: RequestKind
  brief?: BriefSection[]
  features?: FeatureKey[]
  details?: RequestDetails
  budget?: { mode: 'estimate' | 'range'; min_minor?: number; max_minor?: number }
  timeline?: string
  start_date?: string | null
  visibility?: 'public' | 'invite'
  requirements?: { certified?: boolean; rated?: boolean; overlap?: boolean }
  questions?: string[]
  invited?: string[]
  listing_allowed?: boolean
}
export interface BuildRequest extends Required<Omit<RequestInput, 'invited' | 'start_date'>> {
  id: number
  kind_label: string
  feature_labels: string[]
  start_date: string | null
  currency: string
  status: 'draft' | 'open' | 'closed' | 'ordered'
  posted_at: string | null
  closes_at: string | null
  created_at: string
  /** Why it can't be posted yet (draft only). */
  problems: string[]
  estimates_count: number
  // client view
  invited?: BuilderRef[]
  organization?: string
  approver?: boolean
  // builder view
  invited_you?: boolean
  client?: { orders: number; completed: number; spent_minor: number; payment_verified: boolean }
  my_quote?: { id: number; status: QuoteStatus; version: number } | null
}

export async function listMyRequests(): Promise<{ requests: BuildRequest[] }> { const { data } = await api.get(`${BASE}/requests`); return data }
export async function createRequest(body: RequestInput): Promise<BuildRequest> { const { data } = await api.post(`${BASE}/requests`, body); return data }
export async function getRequest(id: number): Promise<BuildRequest> { const { data } = await api.get(`${BASE}/requests/${id}`); return data }
export async function updateRequest(id: number, body: RequestInput): Promise<BuildRequest> { const { data } = await api.put(`${BASE}/requests/${id}`, body); return data }
export async function postRequest(id: number, windowDays = 7): Promise<BuildRequest> { const { data } = await api.post(`${BASE}/requests/${id}/post`, { window_days: windowDays }); return data }
export async function closeRequest(id: number): Promise<BuildRequest> { const { data } = await api.post(`${BASE}/requests/${id}/close`); return data }
/** Twasta AI drafts the brief (503 BUILD_AI_UNAVAILABLE when no AI is set up — let the person type it). */
export async function draftBrief(body: { title: string; description: string; kind: RequestKind; features: FeatureKey[]; details: RequestDetails }): Promise<{ brief: BriefSection[]; size: 'Small' | 'Medium' | 'Large' }> {
  const { data } = await api.post(`${BASE}/brief`, body); return data
}
export async function compareQuotes(requestId: number): Promise<{ quotes: Quote[] }> { const { data } = await api.get(`${BASE}/requests/${requestId}/quotes`); return data }
export async function requestMessages(requestId: number, builderUserId?: string): Promise<{ messages: TimelineEvent[] }> {
  const { data } = await api.get(`${BASE}/requests/${requestId}/messages`, { params: { builder: builderUserId } }); return data
}
/** Client: pass builder_user_id. Builder: omit it. */
export async function sendRequestMessage(requestId: number, text: string, builderUserId?: string): Promise<TimelineEvent> {
  const { data } = await api.post(`${BASE}/requests/${requestId}/messages`, { text, builder_user_id: builderUserId }); return data
}

// ── quotes ─────────────────────────────────────────────────────────────────

export type QuoteStatus = 'draft' | 'sent' | 'revision_asked' | 'superseded' | 'declined' | 'withdrawn' | 'accepted' | 'not_chosen'
export interface QuoteStage {
  index: number; key: string; name: string; approves: string; pct: number; amount_minor: number
  deliverables: string; weeks: number; client: ClientCharge
}
export interface Quote {
  id: number; request_id: number; version: number; status: QuoteStatus
  builder: BuilderRef
  price_minor: number; currency: string; weeks: number | null
  stages: QuoteStage[]
  terms: BuildTerms
  coverage: Record<string, { included: boolean; note: string }>
  monthly: Monthly | Record<string, never>
  ai_mode: 'included' | 'client'
  note: string; answers: string[]; demos: { name: string; url: string; note: string }[]
  start_date: string | null; valid_until: string | null; sent_at: string | null
  shortlisted: boolean; revision_note: string
  advance_minor: number; advance_due: ClientCharge | null
  expired: boolean
  /** Why it can't be sent yet (draft only). */
  problems: string[]
  versions?: { id: number; version: number; status: QuoteStatus; price_minor: number; weeks: number | null; sent_at: string | null }[]
  // compare only
  covered?: number; brief_total?: number
  // GET /quotes/:id only
  request?: BuildRequest; role?: 'client' | 'builder'; approver?: boolean
}
export interface QuoteInput {
  price_minor?: number
  /** One row per standard stage, in order (row 0 = the advance, no deliverable). */
  plan?: { deliverables: string; weeks: number }[]
  coverage?: Record<string, { included: boolean; note?: string }>
  monthly?: Monthly
  ai_mode?: 'included' | 'client'
  note?: string
  answers?: string[]
  demos?: { name: string; url?: string; note?: string }[]
  start_date?: string | null
}
export async function getQuote(id: number): Promise<Quote> { const { data } = await api.get(`${BASE}/quotes/${id}`); return data }
export async function askRevision(id: number, note: string): Promise<Quote> { const { data } = await api.post(`${BASE}/quotes/${id}/revision`, { note }); return data }
export async function declineQuote(id: number): Promise<Quote> { const { data } = await api.post(`${BASE}/quotes/${id}/decline`); return data }
export async function shortlistQuote(id: number, on: boolean): Promise<Quote> { const { data } = await api.post(`${BASE}/quotes/${id}/shortlist`, { on }); return data }
export async function sendQuote(id: number): Promise<Quote> { const { data } = await api.post(`${BASE}/quotes/${id}/send`); return data }
export async function withdrawQuote(id: number): Promise<Quote> { const { data } = await api.post(`${BASE}/quotes/${id}/withdraw`); return data }

// ── builder ────────────────────────────────────────────────────────────────

export async function findWork(params?: { kind?: RequestKind; q?: string; invited?: boolean }): Promise<{ requests: BuildRequest[]; eligible: string | null }> {
  const { data } = await api.get(`${BASE}/work`, { params }); return data
}
export async function getWork(requestId: number): Promise<{ request: BuildRequest; quote: Quote | null; terms: BuildTerms; eligible: string | null }> {
  const { data } = await api.get(`${BASE}/work/${requestId}`); return data
}
/** Create / edit this builder's draft (editing after sending starts the next version). */
export async function saveQuoteDraft(requestId: number, body: QuoteInput): Promise<Quote> {
  const { data } = await api.put(`${BASE}/work/${requestId}/quote`, body); return data
}

export interface BuilderProfile {
  tenant_id: string; name: string; person: string; initials: string; headline: string; about: string
  skills: string[]; location: string; rate_note: string; available: boolean
  bank?: { holder: string; name: string; last4: string; ifsc: string; pan: string; gstin: string; verified: boolean; changed_at: string | null }
}
export interface BuilderStats { orders: number; completed: number; on_time_pct: number | null; rating: number | null; reviews: number; apps_live: number }
export interface LedgerRow { order_id: number; order_no: string; title: string; stage: string; currency: string; gross_minor: number; fee_minor: number; fee_tax_minor: number; tds_minor: number; net_minor: number; paid_at: string | null }
export interface PayoutRow { id: number; tenant_id: string; currency: string; amount_minor: number; utr: string; method: string; note: string; paid_at: string }
export interface ClientApp { order_id: number; title: string; client: string; status: string; basis: string; seats: number | null; size: string | null; price_minor: number; currency: string; project_id: string; last_charge: { amount_minor: number; hosting_minor: number; fee_minor: number; earn_minor: number } | null }
export interface BuilderDashboard {
  profile: BuilderProfile | null
  stats: BuilderStats
  estimates: (Quote & { request: { id: number; title: string; kind: RequestKind; status: string } | null })[]
  orders: OrderSummary[]
  earnings: { currency: string; balance_minor: number }[]
  ledger: LedgerRow[]
  payouts: PayoutRow[]
  client_apps: ClientApp[]
  invitations: BuildRequest[]
  /** null = may quote; else the reason they can't (plan). */
  eligible: string | null
}
export async function getBuilderDashboard(): Promise<BuilderDashboard> { const { data } = await api.get(`${BASE}/me`); return data }
export async function updateBuilderProfile(body: Partial<Omit<BuilderProfile, 'bank'>> & { display_name?: string; bank?: { holder?: string; name?: string; account?: string; ifsc?: string; pan?: string; gstin?: string } }): Promise<BuilderProfile> {
  const { data } = await api.put(`${BASE}/me/profile`, body); return data
}
export interface PublicBuilder extends BuilderProfile {
  stats: BuilderStats
  reviews: { stars: number; text: string; at: string; title: string; amount_minor: number; currency: string }[]
  history: { title: string; completed_at: string | null; price_minor: number; currency: string }[]
  listings: { name: string; slug: string }[]
}
export async function getBuilder(tenantId: string): Promise<PublicBuilder> { const { data } = await api.get(`${BASE}/builders/${encodeURIComponent(tenantId)}`); return data }

// ── orders ─────────────────────────────────────────────────────────────────

export type OrderStatus = 'placed' | 'in_progress' | 'completed' | 'declined' | 'cancelled'
export type StageStatus = 'pending' | 'payment_due' | 'in_review' | 'changes_requested' | 'approved' | 'released' | 'disputed' | 'refunded'
export interface OrderStage {
  idx: number; key: string; name: string; approves: string; pct: number; amount_minor: number
  deliverables: string; target_date: string | null; status: StageStatus
  collected_minor: number; collected_at: string | null; submitted_at: string | null; submit_note: string
  review_due_at: string | null; approved_at: string | null; approved_by: string; auto_approved: boolean
  released_at: string | null; changes_note: string; dispute_note: string; review: any[]; on_hold: boolean
  client: ClientCharge
  /** Builder / admin view only. */
  payout?: Payout
  released_net_minor?: number | null
}
export interface OrderMoney {
  currency: string; price_minor: number; collected_minor: number; service_fees_minor: number
  released_gross_minor: number; paid_to_builder_minor: number; builder_fees_minor: number
  fee_tax_minor: number; tds_minor: number; refunded_minor: number; held_minor: number; still_to_pay_minor: number
}
export interface ProgressReport {
  id: number; period_label: string; rag: 'green' | 'amber' | 'red'; summary: string
  done: string[]; next: string[]; needs: { text: string; due: string | null; done: boolean }[]
  risks: { level: 'low' | 'medium' | 'high'; text: string }[]
  measured: Measured; sent_at: string | null; acknowledged_at: string | null; acknowledged_by: string
}
export interface Measured { at: string; screens: number; reports: number; dashboards: number; workflows: number; processes: number; pages: number; tests_passed: number; tests_failed: number; tests_objects: number; active_days: number; readable: boolean }
export interface Order {
  id: number; order_no: string; title: string; status: OrderStatus; role: 'client' | 'builder' | 'admin'
  currency: string; price_minor: number; monthly: Monthly | Record<string, never>; terms: BuildTerms; listing_allowed: boolean
  client: { tenant_id: string; name: string; person: string; user_id: string }
  builder: BuilderRef
  signed_name: string | null; signed_at: string | null; accept_by: string | null; accepted_at: string | null
  ended_at: string | null; end_reason: string | null
  request_id: number | null; quote_id: number | null; project_id: string | null; subscription_id: number | null; created_at: string
  stages: OrderStage[]
  /** The stage being worked on (null when every stage is done). */
  current_idx: number | null
  money: OrderMoney
  events: TimelineEvent[]
  reports: ProgressReport[]
  /** The viewer may spend the client organization's money (org admin / project owner). */
  approver: boolean
  can: { accept: boolean; submit: boolean; approve: boolean; signoff: boolean; pay: boolean; changes: boolean; cancel: boolean; install: boolean; report: boolean }
  /** Invoices this side may open (client: payment invoices; builder: Twasta's fee invoices). */
  invoices?: OrderInvoice[]
  /** Builder: open the project in the Workbench. */
  project_path?: string
  /** Client: try the working copy (sample data) while it is built. */
  preview_path?: string
  /** Set on action responses. */
  result?: any
}
export interface OrderInvoice { number: string; kind: 'build_collect' | 'build_fee'; total_minor: number; tax_minor: number; currency: string; issued_at: string | null; note: string }
/** URL of an order invoice (opens the printable HTML). */
export function orderInvoiceUrl(orderId: number, number: string): string {
  return `/api${BASE}/orders/${orderId}/invoices/${encodeURIComponent(number)}`
}
export interface OrderSummary {
  id: number; order_no: string; title: string; status: OrderStatus; currency: string; price_minor: number
  client: string; builder: string; current: { idx: number; name: string; status: StageStatus } | null
  money: OrderMoney; monthly: Monthly | Record<string, never>; subscription_id: number | null; created_at: string
}

/** 402 BUILD_PAYMENT_DUE carries `due_minor`, `balance_minor`, `short_minor` — offer a wallet top-up. */
export async function placeOrder(body: { quote_id: number; signed_name: string; listing_allowed?: boolean }): Promise<Order> {
  const { data } = await api.post(`${BASE}/orders`, body); return data
}
export async function listMyOrders(): Promise<{ as_client: OrderSummary[]; as_builder: OrderSummary[] }> { const { data } = await api.get(`${BASE}/orders`); return data }
export async function getOrder(id: number): Promise<Order> { const { data } = await api.get(`${BASE}/orders/${id}`); return data }
export async function acceptOrder(id: number): Promise<Order> { const { data } = await api.post(`${BASE}/orders/${id}/accept`); return data }
export async function declineOrder(id: number, reason = ''): Promise<Order> { const { data } = await api.post(`${BASE}/orders/${id}/decline`, { reason }); return data }
export async function submitStage(id: number, idx: number, note = ''): Promise<Order> { const { data } = await api.post(`${BASE}/orders/${id}/stages/${idx}/submit`, { note }); return data }
export async function payStage(id: number, idx: number): Promise<Order> { const { data } = await api.post(`${BASE}/orders/${id}/stages/${idx}/pay`); return data }
export async function approveStage(id: number, idx: number, decisions?: any[]): Promise<Order> { const { data } = await api.post(`${BASE}/orders/${id}/stages/${idx}/approve`, { decisions }); return data }
export async function requestStageChanges(id: number, idx: number, note: string, decisions?: any[]): Promise<Order> { const { data } = await api.post(`${BASE}/orders/${id}/stages/${idx}/changes`, { note, decisions }); return data }
export async function disputeStage(id: number, idx: number, note: string): Promise<Order> { const { data } = await api.post(`${BASE}/orders/${id}/stages/${idx}/dispute`, { note }); return data }
export async function cancelOrder(id: number, reason: string): Promise<Order> { const { data } = await api.post(`${BASE}/orders/${id}/cancel`, { reason }); return data }
/** Final sign-off: approves + pays the last stage and installs the app. `result.install_error` is set when the install didn't start (retry with installOrder). */
export async function signOffOrder(id: number, body: { signed_name: string; people?: number; rating?: number; review?: string }): Promise<Order> {
  const { data } = await api.post(`${BASE}/orders/${id}/signoff`, body); return data
}
export async function installOrder(id: number, people?: number): Promise<Order> { const { data } = await api.post(`${BASE}/orders/${id}/install`, { people }); return data }
export async function sendOrderMessage(id: number, text: string): Promise<TimelineEvent> { const { data } = await api.post(`${BASE}/orders/${id}/messages`, { text }); return data }
export async function sendReport(id: number, body: { rag: 'green' | 'amber' | 'red'; summary?: string; period_label?: string; done?: string[]; next?: string[]; needs?: { text: string; due?: string }[]; risks?: { level: 'low' | 'medium' | 'high'; text: string }[] }): Promise<ProgressReport> {
  const { data } = await api.post(`${BASE}/orders/${id}/reports`, body); return data
}
export async function measureOrder(id: number): Promise<Measured> { const { data } = await api.get(`${BASE}/orders/${id}/measure`); return data }
export async function acknowledgeReport(id: number, reportId: number): Promise<ProgressReport> { const { data } = await api.post(`${BASE}/orders/${id}/reports/${reportId}/ack`); return data }
export async function toggleReportNeed(id: number, reportId: number, index: number): Promise<ProgressReport> { const { data } = await api.post(`${BASE}/orders/${id}/reports/${reportId}/needs/${index}`); return data }

// ── instance admin ─────────────────────────────────────────────────────────

export interface ReleaseItem {
  order_id: number; order_no: string; title: string; idx: number; stage: string; pct: number; status: StageStatus
  currency: string; gross_minor: number; payout: Payout; client: string; builder: string
  approved_by: string; approved_at: string | null; dispute_note: string; on_hold: boolean; hold_reason: string
  checks: { key: string; ok: boolean; label: string }[]; ready: boolean
  bank: { last4: string; verified: boolean }
}
export async function adminReleases(): Promise<{ queue: ReleaseItem[] }> { const { data } = await api.get(`${BASE}/admin/releases`); return data }
export async function adminOrders(status?: OrderStatus): Promise<{ orders: OrderSummary[] }> { const { data } = await api.get(`${BASE}/admin/orders`, { params: { status } }); return data }
export async function adminOrder(id: number): Promise<Order> { const { data } = await api.get(`${BASE}/admin/orders/${id}`); return data }
export async function adminRelease(id: number, idx: number): Promise<any> { const { data } = await api.post(`${BASE}/admin/orders/${id}/stages/${idx}/release`); return data }
export async function adminHold(id: number, idx: number, on: boolean, reason = ''): Promise<{ on_hold: boolean; hold_reason: string | null }> {
  const { data } = await api.post(`${BASE}/admin/orders/${id}/stages/${idx}/hold`, { on, reason }); return data
}
export async function adminResolve(id: number, idx: number, outcome: 'approve' | 'refund' | 'changes', note: string): Promise<any> {
  const { data } = await api.post(`${BASE}/admin/orders/${id}/stages/${idx}/resolve`, { outcome, note }); return data
}
export interface PayoutBoardRow { tenant_id: string; builder: string; currency: string; balance_minor: number; bank: NonNullable<BuilderProfile['bank']>; last_payout: PayoutRow | null }
export interface TdsRow { tenant_id: string; builder: string; pan: string; quarter: string; currency: string; tds_minor: number }
export async function adminPayouts(): Promise<{ builders: PayoutBoardRow[]; tds: TdsRow[] }> { const { data } = await api.get(`${BASE}/admin/payouts`); return data }
export async function adminRecordPayout(body: { tenant_id: string; currency: string; amount_minor: number; utr: string; method?: string; note?: string }): Promise<PayoutRow> {
  const { data } = await api.post(`${BASE}/admin/payouts`, body); return data
}
export async function adminVerifyBank(tenantId: string, verified: boolean): Promise<BuilderProfile> {
  const { data } = await api.post(`${BASE}/admin/builders/${encodeURIComponent(tenantId)}/verify-bank`, { verified }); return data
}
export async function adminBuilderPayouts(tenantId: string): Promise<{ payouts: PayoutRow[]; ledger: LedgerRow[] }> {
  const { data } = await api.get(`${BASE}/admin/builders/${encodeURIComponent(tenantId)}/payouts`); return data
}

// ── labels ─────────────────────────────────────────────────────────────────

export const KIND_LABELS: Record<RequestKind, string> = {
  new: 'A new app', change: 'Changes to an app I use', site: 'A website or portal', analytics: 'Analytics on my existing data',
}
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  phone: 'Works on phones', offline: 'Works without internet', approvals: 'Approvals', alerts: 'WhatsApp or email alerts',
  import: 'Bring in existing data', dash: 'Dashboards and reports', portal: 'Customer or partner login', integrate: 'Integrate with my system',
}
export const STAGE_STATUS_LABELS: Record<StageStatus, string> = {
  pending: 'Not started', payment_due: 'Payment needed', in_review: 'Waiting for approval', changes_requested: 'Changes asked',
  approved: 'Approved — being paid', released: 'Paid to the builder', disputed: 'Disputed — frozen', refunded: 'Refunded',
}
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  placed: 'Waiting for the builder', in_progress: 'Being built', completed: 'Installed', declined: 'Not accepted — refunded', cancelled: 'Cancelled',
}
